import type { Hlc, Id } from '../types';
import type { LwwRow } from './entities';

/**
 * Nakłada zmianę pól na wiersz: pole zmienia się tylko wtedy, gdy znacznik zmiany
 * jest późniejszy niż znacznik ostatniego zapisu tego pola.
 * Zwraca nowy wiersz i listę pól, które faktycznie się zmieniły.
 */
export function applyFields<T extends LwwRow>(
  row: T | undefined,
  id: Id,
  fields: Record<string, unknown>,
  hlc: Hlc,
): { row: T; changed: string[] } {
  const next = { ...(row ?? { id, fieldHlc: {} }), fieldHlc: { ...(row?.fieldHlc ?? {}) } } as T;
  const changed: string[] = [];
  for (const [field, value] of Object.entries(fields)) {
    if (field === 'id' || field === 'fieldHlc') continue;
    const current = next.fieldHlc[field];
    if (current !== undefined && current >= hlc) continue;
    (next as Record<string, unknown>)[field] = value;
    next.fieldHlc[field] = hlc;
    changed.push(field);
  }
  return { row: next, changed };
}

/** Scala dwie wersje wiersza pole po polu (wygrywa późniejszy znacznik). */
export function mergeRow<T extends LwwRow>(local: T | undefined, remote: T): T {
  if (!local) return { ...remote, fieldHlc: { ...remote.fieldHlc } };
  const out = { ...local, fieldHlc: { ...local.fieldHlc } } as T;
  for (const [field, hlc] of Object.entries(remote.fieldHlc)) {
    const mine = out.fieldHlc[field];
    if (mine === undefined || hlc > mine) {
      (out as Record<string, unknown>)[field] = (remote as Record<string, unknown>)[field];
      out.fieldHlc[field] = hlc;
    }
  }
  return out;
}

/** Najpóźniejszy znacznik w wierszu (do porównań i diagnostyki). */
export function rowHlc(row: LwwRow): Hlc | null {
  let max: Hlc | null = null;
  for (const h of Object.values(row.fieldHlc)) if (max === null || h > max) max = h;
  return max;
}
